import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import { sendaGoal, type BattleState } from '@somnhold/sim';
import { clearOfTags, markBoxes, pinMark, spreadMarks, type TagBox } from './mark-pin.ts';
import { ErmitaHud } from './ermita-hud.ts';
import { VigiliaHud } from './vigilia-hud.ts';
import { t } from '../i18n.ts';
import { clear, h, setStyle } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';
import { BridgeHud, type BridgeActions, type BridgeAnchors } from './bridge-hud.ts';
import { FortHud, type FortAnchors } from './fort-hud.ts';
import { SlideHud, type SlideAnchors } from './slide-hud.ts';
import { PassHud, type PassAnchors } from './pass-hud.ts';
import { AltarHud, type AltarAnchors } from './altar-hud.ts';
import { SaltHud, type SaltAnchors } from './salt-hud.ts';
import { ArchHud, type ArchAnchors } from './arch-hud.ts';
import { MonasteryHud } from './monastery-hud.ts';
import { SeventhHud } from './seventh-hud.ts';
import { TallerHud } from './taller-hud.ts';
import { AsterHud } from './aster-hud.ts';
import { GaloHud } from './galo-hud.ts';
import { BiblioHud } from './biblio-hud.ts';
import { CableHud } from './cable-hud.ts';
import { CorazonHud } from './corazon-hud.ts';
import { OjosHud } from './ojos-hud.ts';
import { TormoHud } from './tormo-hud.ts';
import { CazaHud } from './caza-hud.ts';
import { VertHud } from './vertebras-hud.ts';
import { BoteHud } from './bote-hud.ts';
import { IslaHud } from './isla-hud.ts';
import { RocaHud } from './roca-hud.ts';
import { CaminoHud } from './camino-hud.ts';
import { PasarelaHud } from './pasarela-hud.ts';
import { NieveHud } from './nieve-hud.ts';
import { DruidHud } from './druid-hud.ts';
import { HornosHud } from './hornos-hud.ts';
import { OtonoHud } from './otono-hud.ts';
import { CopaHud } from './copa-hud.ts';
import { RaicesHud } from './raices-hud.ts';

/** How long a line said in a path's fight stays over the speaker (ms). */
const SAY_MS = 5200;

/** Ticks left as a clock, «2:28» (a long count of seconds, «148 s», read as a number, not as time). */
const clock = (ticks: number): string => {
  const s = Math.max(0, Math.ceil(ticks / TICK_HZ));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export interface SendaHudInfo {
  /** The act's text prefix (`senda.<id>.<ch>.<act>`): its title, its goal, how it is lost and what is said in it. */
  key: string;
  /** Who is who (the path's people, the hero, a memory's face). */
  names: Record<string, string>;
}

/** Where the ledge's marks hang (render's SpineLayer anchors, world points). */
export interface SendaAnchors {
  gaps: THREE.Vector3[];
  rock: THREE.Vector3 | null;
  cornice: THREE.Vector3 | null;
  house: THREE.Vector3 | null;
}

/**
 * A path's fight on screen (docs/SENDAS.md): its own plate, alone at the top (no incursion bars): the act's goal and
 * how far along it is, how it is lost, and the path's people with their life in blows; what they say (the sim's 'say'
 * events, so a replay says the same) in a bubble over whoever says it, the person on the field or the hero, or (a
 * voice from off the field, Wenn at the door) a line under the plate. The ledge (the wedges) adds its own: the shields
 * in hand, the counter under the cornice, the ones in the house, the column's clock; and over the board, a mark on
 * each gap and on the loose rock, to tap (the hero goes and does it: plants, pulls, pushes), with a ring that fills as
 * it works, and the counter hanging from the cornice. Plain DOM, updated once a tick; the marks follow the camera.
 */
export class SendaHud {
  readonly el: HTMLElement;
  readonly bubbles = h('div.senda-bubbles', { 'aria-live': 'polite' });
  /** The ledge's marks over the board (tappable). */
  readonly marks = h('div.senda-marks');
  private fill = h('i');
  private progress = h('span.sh-prog');
  private people = h('ul.sh-people');
  private rows = new Map<string, { li: HTMLElement; pips: HTMLElement; n: HTMLElement; last: number }>();
  private said: { who: string; el: HTMLElement; until: number; w: number; h: number }[] = [];
  private voice = h('div.sh-voice');
  private voiceUntil = 0;
  private v = new THREE.Vector3();
  // The ledge.
  private wedge: {
    hand: HTMLElement;
    count: HTMLElement;
    countFill: HTMLElement[];
    inside: HTMLElement;
    insideFill: HTMLElement[];
    clock: HTMLElement;
    gaps: HTMLButtonElement[];
    rock: HTMLButtonElement;
    cornice: HTMLElement;
  } | null = null;
  /** The cut bridge's own panel and marks (chapter 2 of the warrior's path). */
  readonly bridge: BridgeHud | null = null;
  /** The fort's (chapter 3): the climb, the roof that tips. */
  readonly fort: FortHud | null = null;
  /** The avalanche's (chapter 4): the loose slope, the ice cave, the large wedges. */
  readonly slide: SlideHud | null = null;
  /** The Pass of the Rift's (chapter 5): the ledge, Ruy's line, the beam, the Ventisquero. */
  readonly pass: PassHud | null = null;
  /** The Way of the Altars' (the paladin's chapter 1): the canals and the water that goes, the second altar. */
  readonly altar: AltarHud | null = null;
  /** The salt flats' (the paladin's chapter 2): the fog, the gates, Marit's salt. */
  readonly salt: SaltHud | null = null;
  /** The bridge's altar's (the paladin's chapter 3): the aura, the bands, the cistern, the rain, the levers, the mammoths. */
  readonly arch: ArchHud | null = null;
  /** The monastery of the waterfall's (the paladin's chapter 4): Ser Edda's cover, the cells' salt, the cistern's gates. */
  readonly monastery: MonasteryHud | null = null;
  /** The seventh altar's (the paladin's chapter 5): the rubble, the cistern's gates, La Que Huyó's lines and the oath. */
  readonly seventh: SeventhHud | null = null;
  /** Nadd's workshop's (the artificer's chapter 1): Grillo on the ledge, the halls' grilles, the gear train. */
  readonly taller: TallerHud | null = null;
  /** The burnt library's (the mage's chapter 1): the cellars' halls and the page's glow, the seals and the scribes. */
  readonly biblio: BiblioHud | null = null;
  /** The cable's (the artificer's chapter 2): the crane's arm, the car's cable and its tilts, the gearbox. */
  readonly cable: CableHud | null = null;
  /** The heart's (the artificer's chapter 3): the deep road's vents, the hammer freed, the cams and La Tenaza. */
  readonly corazon: CorazonHud | null = null;
  /** The eyes' (the artificer's chapter 4): Grada's struts in the memory, the mine's carts, the drills and La Barrena. */
  readonly ojos: OjosHud | null = null;
  /** Tormo's (the artificer's chapter 5): his knees, the winch, the shell, the plates. */
  readonly tormo: TormoHud | null = null;
  /** The Long Hunt's (the barbarian's chapter 1): the herd and the drags, the tracks, the rain, Terrón, the howler. */
  readonly caza: CazaHud | null = null;
  /** The Vertebrae's (the barbarian's chapter 2): the gusts and Terrón, the arches' arrivals and Sigrun's marks, the boat and the howlers. */
  readonly vert: VertHud | null = null;
  /** Bera's boat (the barbarian's chapter 3): the wake and the forks, the oars; the big waves and the swept; the old howler. */
  readonly bote: BoteHud | null = null;
  /** The White Island's (the barbarian's chapter 4): the bands, the cracks and the necks; the tracks under the ice; the Seal and the fire. */
  readonly isla: IslaHud | null = null;
  /** The black rock's (the barbarian's chapter 5): Ulla and her horn, the ledges, the arches, the bed, the Grey Wolf. */
  readonly roca: RocaHud | null = null;
  /** The cleric's last road (its chapter 1): the stretcher, the heals that reach Tessa, the limpets, the chapel's marks. */
  readonly camino: CaminoHud | null = null;
  /** Its chapter 2: the slingers and the chapels, the moon on the walkway's spans and posts, the mangonel and the planks. */
  readonly pasarela: PasarelaHud | null = null;
  /** Its chapter 3: Tessa's cold, the braziers, the frozen mirrors and their bases, the watcher and the family. */
  readonly nieve: NieveHud | null = null;
  /** Its chapter 4: the vigil's lamps, the Doliente and her wail; Aud's mirror, the altar and the door, the singer. */
  readonly ermita: ErmitaHud | null = null;
  readonly vigilia: VigiliaHud | null = null;
  /** The Circle's wood (the druid's chapter 1): the thaw, Olmo's clearing, the root bridge. */
  readonly druid: DruidHud | null = null;
  /** The Slope of the kilns (the druid's chapter 2): the kilns, the strip, the counterfire. */
  readonly hornos: HornosHud | null = null;
  /** The autumn wood (the druid's chapter 3): under the leaves, the memory, what lies below. */
  readonly otono: OtonoHud | null = null;
  /** The Grandsire's crown (the druid's chapter 4): the boughs, the hollow, the sap of the crown. */
  readonly copa: CopaHud | null = null;
  /** Inside the Grandsire (the druid's chapter 5): the buds below, the Carcoma. */
  readonly raices: RaicesHud | null = null;
  /** Aster's stones (the ranger's chapter 1): the knots, the fog, the fallen cairn. */
  readonly aster: AsterHud | null = null;
  /** The flank's cliff (the ranger's chapter 2): the cornice, the wall, the night on the wall. */
  readonly galo: GaloHud | null = null;

  constructor(
    private info: SendaHudInfo,
    s: BattleState,
    /** A line said by a hero on the field (the hero itself, a memory): over its name tag. */
    private heroSay: (text: string) => boolean,
    /** The ledge: the hero sent to a gap (its index) or to the rock (the gaps' count). */
    private act: (at: number) => void = () => undefined,
    /** The cut bridge's taps. */
    bridgeActs?: BridgeActions,
  ) {
    const k = info.key;
    const w = s.rules?.senda?.wedge;
    const sr = s.rules?.senda;
    const bridge = sr?.hooks || sr?.gust || sr?.weight ? new BridgeHud(s, this.marks, bridgeActs ?? { mark: () => undefined, aim: () => undefined }) : null;
    this.bridge = bridge;
    const fort = sr?.slope || sr?.roof ? new FortHud(s, this.marks, bridgeActs ?? { mark: () => undefined, aim: () => undefined }) : null;
    this.fort = fort;
    const slide = sr?.slide || sr?.cave ? new SlideHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.slide = slide;
    const pass = sr?.crag || sr?.line || sr?.beam || sr?.storm ? new PassHud(s, this.marks, (at) => bridgeActs?.mark(at), info.names) : null;
    this.pass = pass;
    const altar = sr?.canal || sr?.kindle ? new AltarHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.altar = altar;
    const salt = sr?.salt ? new SaltHud(s, this.marks, (at) => bridgeActs?.mark(at), (on) => bridgeActs?.aim(on)) : null;
    this.salt = salt;
    const arch = sr?.arch ? new ArchHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.arch = arch;
    const monastery = sr?.monastery ? new MonasteryHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.monastery = monastery;
    const seventh = sr?.seventh ? new SeventhHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.seventh = seventh;
    const taller = sr?.cornice || sr?.workshop || sr?.gears ? new TallerHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.taller = taller;
    const biblio = sr?.soot || sr?.seals ? new BiblioHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.biblio = biblio;
    const cable = sr?.crane || sr?.gondola || sr?.gearbox ? new CableHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.cable = cable;
    const corazon = sr?.vent || sr?.ember || sr?.cams ? new CorazonHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.corazon = corazon;
    const ojos = sr?.struts || sr?.carts || sr?.drills ? new OjosHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.ojos = ojos;
    const tormo = sr?.knees || sr?.torno || sr?.shell || sr?.plates ? new TormoHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.tormo = tormo;
    const caza = sr?.pens || sr?.trail ? new CazaHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.caza = caza;
    const vert = sr?.vert ? new VertHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.vert = vert;
    const bote = sr?.bote ? new BoteHud(s, this.marks, this.info.names, (at) => bridgeActs?.mark(at)) : null;
    this.bote = bote;
    const isla = sr?.isla ? new IslaHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.isla = isla;
    const roca = sr?.roca ? new RocaHud(s, this.marks, (at) => bridgeActs?.mark(at), bridgeActs?.ground) : null;
    this.roca = roca;
    const camino = sr?.litter ? new CaminoHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.camino = camino;
    const pasarela = sr?.litter?.ridge || sr?.litter?.walk || sr?.litter?.span ? new PasarelaHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.pasarela = pasarela;
    const nieve = sr?.litter?.snow ? new NieveHud(s, this.marks, (at) => bridgeActs?.mark(at), info.names) : null;
    this.nieve = nieve;
    const ermita = sr?.litter?.hermit && !sr.litter.hermit.vela ? new ErmitaHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.ermita = ermita;
    const vigilia = sr?.litter?.hermit?.vela ? new VigiliaHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.vigilia = vigilia;
    const druid = sr?.druid ? new DruidHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.druid = druid;
    const hornos = sr?.summer ? new HornosHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.hornos = hornos;
    const otono = sr?.autumn ? new OtonoHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.otono = otono;
    const copa = sr?.winter ? new CopaHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.copa = copa;
    const raices = sr?.deep ? new RaicesHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.raices = raices;
    const aster = sr?.cairns || sr?.echo || sr?.fallen ? new AsterHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.aster = aster;
    const galo = sr?.cliff ? new GaloHud(s, this.marks, (at) => bridgeActs?.mark(at)) : null;
    this.galo = galo;
    // (The second altar keeps the plate's own bar: its goal is the clock.)
    const own = !!bridge || !!fort || !!slide || !!pass || !!sr?.canal || sr?.seventh?.kind === 'well' || !!sr?.gears || !!sr?.gearbox || !!sr?.cams || !!sr?.drills || !!tormo || !!sr?.seals || !!sr?.soot || !!aster || !!galo;
    this.el = h(
      `div.senda-hud${w ? '.wedge' : ''}`,
      { 'data-test': 'senda-hud' },
      h('div.sh-goal', {}, h('span.sh-ico', { html: ICONS.target }), h('span.sh-text', {}, t(`${k}.win`)), w || own ? '' : this.progress),
      w || own ? '' : h('div.bar.sh-bar', {}, this.fill),
      w ? this.buildWedge(s) : '',
      bridge ? bridge.panel : '',
      fort ? fort.panel : '',
      slide ? slide.panel : '',
      pass ? pass.panel : '',
      altar ? altar.panel : '',
      salt ? salt.panel : '',
      arch ? arch.panel : '',
      monastery ? monastery.panel : '',
      seventh ? seventh.panel : '',
      taller ? taller.panel : '',
      cable ? cable.panel : '',
      corazon ? corazon.panel : '',
      ojos ? ojos.panel : '',
      tormo ? tormo.panel : '',
      caza ? caza.panel : '',
      vert ? vert.panel : '',
      bote ? bote.panel : '',
      isla ? isla.panel : '',
      roca ? roca.panel : '',
      camino ? camino.panel : '',
      pasarela ? pasarela.panel : '',
      nieve ? nieve.panel : '',
      ermita ? ermita.panel : '',
      vigilia ? vigilia.panel : '',
      druid ? druid.panel : '',
      hornos ? hornos.panel : '',
      otono ? otono.panel : '',
      copa ? copa.panel : '',
      raices ? raices.panel : '',
      aster ? aster.panel : '',
      galo ? galo.panel : '',
      biblio ? biblio.panel : '',
      this.people,
      h('p.sh-lose', {}, h('span', { html: ICONS.skull }), t('senda.hud.lose', { text: t(`${k}.lose`) })),
      this.voice,
    );
    this.update(s);
  }

  /** The ledge's own lines in the plate, and its marks over the board. */
  private buildWedge(s: BattleState): HTMLElement {
    const r = s.rules?.senda?.wedge;
    if (!r) return h('div');
    const cells = (n: number, cls: string): HTMLElement[] => Array.from({ length: n }, () => h(`i.${cls}`));
    const countFill = cells(r.need, 'wc');
    const insideFill = cells(r.lose, 'wi');
    const hand = h('span.wg-hand');
    const count = h('b.wg-n');
    const inside = h('b.wg-n');
    const clock = h('p.wg-clock');
    const gaps = r.gaps.map((_, i) =>
      h('button.wg-gap', { type: 'button', 'data-test': `wedge-gap-${i}`, 'aria-label': t('senda.wedge.gapHint'), onpointerup: (e: Event) => (e.stopPropagation(), this.act(i)) }, h('span.wg-ring'), h('span.wg-shield', { html: ICONS.shield })),
    ) as HTMLButtonElement[];
    const rock = h('button.wg-rock', { type: 'button', 'data-test': 'wedge-rock', onpointerup: (e: Event) => (e.stopPropagation(), this.act(r.gaps.length)) }, h('span.wg-ring'), h('b', {}, t('senda.wedge.rock')), h('small', {}, t('senda.wedge.rockHint'))) as HTMLButtonElement;
    const cornice = h('div.wg-cornice', {}, h('b', {}, '0'), h('small', {}, `/ ${r.need}`));
    this.marks.append(...gaps, rock, cornice);
    this.wedge = { hand, count, countFill, inside, insideFill, clock, gaps, rock, cornice };
    return h(
      'div.sh-wedge',
      {},
      h('div.wg-row', {}, h('span.wg-label', {}, t('senda.wedge.shields')), hand),
      h('div.wg-row', {}, h('span.wg-label', {}, t('senda.wedge.count')), h('span.wg-meter', {}, ...countFill), count),
      h('div.wg-row', {}, h('span.wg-label', {}, t('senda.wedge.inside')), h('span.wg-meter.bad', {}, ...insideFill), inside),
      clock,
    );
  }

  /** Once a tick: the goal's progress, each person's blows, the ledge's numbers, and the lines just said. */
  update(s: BattleState): void {
    const g = sendaGoal(s);
    const kind = s.rules?.senda?.goal.kind;
    const k = Math.max(0, Math.min(1, g.done / Math.max(1, g.need)));
    setStyle(this.fill, 'width', `${(k * 100).toFixed(1)}%`);
    const text =
      kind === 'time'
        ? t('senda.hud.time', { s: Math.floor(Math.min(g.done, g.need) / TICK_HZ), of: Math.round(g.need / TICK_HZ) })
        : kind === 'work'
          ? t('senda.hud.work', { n: g.done, of: g.need })
          : kind === 'boss'
            ? t('senda.hud.boss', { n: Math.floor(k * 100) })
            : kind === 'drain'
              ? t('senda.salt.goalDrain', { n: g.need - g.done })
              : kind === 'house'
                ? t('senda.salt.goalHouse', { n: g.need - g.done })
                : kind === 'ends'
                  ? t('senda.arch.goalEnds', { n: g.need - g.done })
                  : kind === 'cistern'
                    ? t('senda.arch.goalCistern', { n: g.done, of: g.need })
                    : kind === 'mammoth'
                      ? t('senda.arch.goalMammoth', { n: g.done, of: g.need })
                      : kind === 'tide'
                        ? t('senda.mon.goalTide', { t: clock(g.need - g.done) })
                        : kind === 'cell'
                          ? t('senda.mon.goalCell')
                          : kind === 'sixth'
                            ? g.done >= g.need
                              ? t('senda.mon.goalSentinel')
                              : g.need - g.done === 1
                                ? t('senda.mon.goalSixthOne')
                                : t('senda.mon.goalSixth', { n: g.need - g.done })
                            : kind === 'rubble'
                              ? t('senda.hud.seventh.piles', { n: g.done, of: g.need })
                              : kind === 'reach'
                                ? t('senda.hud.seventh.rooms', { n: Math.min(g.need, g.done + 1), of: g.need })
                                : kind === 'ring'
                                  ? ''
                                  : kind === 'fled'
                                    ? t('senda.hud.boss', { n: Math.floor(k * 100) })
                                    : kind === 'grillo'
                                      ? t('senda.taller.goalGrillo', { n: Math.floor(k * 100) })
                                      : kind === 'tormo'
                                        ? t('senda.taller.goalHalls', { n: Math.min(g.need, g.done + 1), of: g.need })
                                        : kind === 'crane'
                                          ? t('senda.cable.goalCrane', { n: Math.floor(k * 100) })
                                          : kind === 'span'
                                            ? t('senda.cable.goalSpan', { n: Math.floor(k * 100) })
                                            : kind === 'marrow'
                                              ? t('senda.heart.goalRoad', { n: Math.floor(k * 100) })
                                              : kind === 'unjam'
                                                ? t('senda.heart.goalRiver', { n: Math.floor(k * 100) })
                                                : kind === 'struts'
                                                  ? t('senda.ojos.goalStruts', { n: g.done, of: g.need })
                                                  : kind === 'drillHall'
                                                    ? t('senda.ojos.goalCarts', { n: Math.min(g.done, g.need - 1), of: g.need - 1 })
                                        : kind === 'herd'
                                          ? t('senda.caza.goalHerd', { n: g.done, of: g.need })
                                          : kind === 'trail'
                                            ? t('senda.caza.goalTrail', { n: g.done, of: g.need })
                                            : kind === 'way'
                                              ? t('senda.vert.goalWay', { n: Math.floor(k * 100) })
                                              : kind === 'leaps'
                                                ? t('senda.vert.goalLeaps', { n: Math.min(g.need - 1, g.done), of: g.need - 1 })
                                                : kind === 'boat'
                                                  ? t('senda.vert.goalBoat', { n: Math.floor(k * 100) })
                                                  : kind === 'wake'
                                                    ? t('senda.bote.goalWake', { n: g.done, of: g.need })
                                                    : kind === 'waves'
                                                      ? t('senda.bote.goalWaves', { n: g.done, of: g.need })
                                                      : kind === 'elder'
                                                        ? t('senda.bote.goalElder', { n: Math.floor(k * 100) })
                                                        : kind === 'floes'
                                                          ? t('senda.isla.goalFloes', { n: g.done, of: g.need })
                                                          : kind === 'lagoon'
                                                            ? t('senda.isla.goalLagoon', { n: g.done, of: g.need })
                                                            : kind === 'seal'
                                                              ? t('senda.isla.goalSeal', { n: Math.floor(k * 100) })
                                                              : kind === 'climb'
                                                                ? t('senda.roca.goalClimb', { n: Math.floor(k * 100) })
                                                                : kind === 'mouths'
                                                                  ? t('senda.roca.goalMouths', { n: g.done, of: g.need })
                                                                  : kind === 'bed'
                                                                    ? t('senda.roca.goalBed', { n: g.done, of: g.need })
                                                                    : kind === 'wolf'
                                                                      ? t('senda.roca.goalWolf', { n: Math.floor(k * 100) })
                                        : kind === 'litter'
                                          ? t('senda.camino.goalLitter', { n: Math.floor(k * 100) })
                                          : kind === 'lamp'
                                            ? s.rules?.senda?.litter?.snow
                                              ? t(g.done >= g.need ? 'senda.camino.goalDoorLit' : 'senda.camino.goalDoor')
                                              : t(g.done >= g.need ? 'senda.camino.goalLit' : 'senda.camino.goalLamp')
                                            : kind === 'night'
                                              ? t('senda.camino.goalNight', { t: clock(g.need - g.done) })
                                              : kind === 'lamps'
                                                ? t('senda.camino.goalLamps', { n: g.done, of: g.need })
                                                : kind === 'procession'
                                                  ? t('senda.camino.goalProcession', { n: g.done, of: g.need })
                                                  : kind === 'mourn'
                                                    ? t('senda.camino.goalDawn', { t: clock(g.need - g.done) })
                                        : kind === 'glade'
                                          ? t('senda.bosque.goalGlade', { n: Math.floor(k * 100) })
                                          : kind === 'face'
                                            ? t('senda.bosque.goalFace', { n: Math.floor(k * 100) })
                                            : kind === 'bridge'
                                              ? g.done * 3 >= g.need * 2
                                                ? t('senda.bosque.goalWeave', { n: Math.floor(((g.done * 3 - g.need * 2) * 100) / Math.max(1, g.need)) })
                                                : t('senda.bosque.goalBridge', { n: Math.floor((g.done * 3) / Math.max(1, g.need)) })
                                              : kind === 'kilns'
                                                ? t('senda.hornos.goalKilns', { n: g.done, of: g.need })
                                                : kind === 'strip'
                                                  ? t('senda.hornos.goalStrip', { n: g.done, of: g.need })
                                                  : kind === 'cone'
                                                    ? t('senda.hornos.goalCone', { n: g.done })
                                                    : kind === 'maple'
                                                      ? t('senda.otono.goalMaple', { n: Math.floor(k * 100) })
                                                      : kind === 'houses'
                                                        ? t('senda.otono.goalHouses', { n: g.done, of: g.need })
                                                        : kind === 'rot'
                                                          ? t('senda.otono.goalRot', { n: g.done, of: g.need })
                                                          : kind === 'crown'
                                                            ? t('senda.copa.goalCrown', { n: Math.floor(k * 100) })
                                                            : kind === 'bandage'
                                                              ? t('senda.copa.goalBandage', { n: Math.floor(k * 100) })
                                                              : kind === 'sap'
                                                                ? t('senda.copa.goalSap', { n: g.done, of: g.need })
                                                                : kind === 'deepMouths'
                                                                  ? t('senda.raices.goalMouths', { n: g.done, of: g.need })
                                                                  : kind === 'net'
                                                                    ? t('senda.otono.goalRot', { n: g.done, of: g.need })
                                                                    : kind === 'carcoma'
                                                                      ? t('senda.hud.boss', { n: Math.floor(k * 100) })
                                                    : kind === 'catalog'
                                                      ? t('senda.biblio.goalHalls', { n: Math.min(g.need, g.done + 1), of: g.need })
                                    : // «n de m caídas» counts the fallen of the wave: only a fight won by clearing it says that (a goal
                              // without its own line read «143 de 1800 caídas», ticks counted as the fallen).
                              kind === 'clear' || !kind
                              ? t('senda.hud.clear', { n: g.done, of: g.need })
                              : '';
    if (this.progress.textContent !== text) this.progress.textContent = text;
    const rules = s.rules?.senda;
    (s.senda?.npcs ?? []).forEach((n, i) => {
      // Someone kept out of the fight (Wenn waiting) has no blows to count; someone not out yet (Nuño), not yet.
      if (rules?.npcs[i]?.safe) return;
      // (The bridge's upper keepers, Varo and his knights, aren't vital: their bars over them on the field say enough, and a phone's plate stays short.)
      if (rules?.arch && rules.npcs[i]?.vital === false) return;
      // (Ser Edda in the memory doesn't fall: no life to count; the plate says whether she covers the squire.)
      if (rules?.monastery && rules.npcs[i]?.vital === false) return;
      // (Nor the seventh altar's knights, salt folk and Varo: their bars over them; a phone's plate keeps Marit and the fight.)
      if (rules?.seventh && rules.npcs[i]?.vital === false) return;
      // (Nor Grada's borrowed golems in the memory: the plate's own row says what each does.)
      if (rules?.struts && rules.npcs[i]?.vital === false) return;
      // (Nor Terrón on the tracks: the plate's own row says what he can do.)
      if (rules?.trail && rules.npcs[i]?.vital === false) return;
      // (Nor Terrón and Sten on the Vertebrae: the plate's own rows say what matters.)
      if (rules?.vert && rules.npcs[i]?.vital === false) return;
      // (Nor the boat's people: the plate's own rows say who is in the water.)
      if (rules?.bote && rules.npcs[i]?.vital === false) return;
      // (Nor the island's: Bera, Terrón and Sten by the camp, the plate's own rows say what matters.)
      if (rules?.isla && rules.npcs[i]?.vital === false) return;
      // (Nor the rock's: Ulla's own row is the plate's; Ivar's bar over him on the summit.)
      if (rules?.roca) return;
      // (Nor the cleric's vigil's people at the door and by the candle: a phone's plate keeps Tessa and the fight.)
      if (rules?.litter?.hermit?.vela && rules.npcs[i]?.vital === false) return;
      // (Nor the Carcoma's colliers: one line in the plate counts them.)
      if (rules?.deep?.carcoma && rules.npcs[i]?.vital === false) return;
      let row = this.rows.get(n.id);
      if (n.away) {
        if (row) row.li.hidden = true;
        return;
      }
      if (row) row.li.hidden = false;
      if (!row) {
        const pips = h('span.sh-pips');
        const num = h('small');
        const li = h('li', { 'data-npc': n.id }, h('b', {}, this.info.names[n.id] ?? n.id), pips, num);
        this.people.append(li);
        row = { li, pips, n: num, last: -1 };
        this.rows.set(n.id, row);
      }
      if (row.last === n.hp) return;
      // A blow just landed: the row flashes.
      if (row.last > n.hp) {
        row.li.classList.remove('struck');
        void row.li.offsetWidth;
        row.li.classList.add('struck');
      }
      row.last = n.hp;
      clear(row.pips);
      // A pip a blow up to 20; past that, a bar.
      if (n.maxHp <= 20) for (let j = 0; j < n.maxHp; j++) row.pips.append(h(`i${j < n.hp ? '.on' : ''}`));
      else row.pips.append(h('span.bar.sh-npc-bar', {}, h('i', { style: `width:${(100 * n.hp) / n.maxHp}%` })));
      row.n.textContent = n.hp > 1 ? t('senda.hud.hits', { n: n.hp }) : n.hp === 1 ? t('senda.hud.hitsOne') : t('senda.hud.down');
      row.li.classList.toggle('down', n.hp <= 0);
      row.li.classList.toggle('low', n.hp > 0 && n.hp * 3 <= n.maxHp);
    });
    this.updateWedge(s);
    this.bridge?.update(s);
    this.fort?.update(s);
    this.slide?.update(s);
    this.pass?.update(s);
    this.altar?.update(s);
    this.salt?.update(s);
    this.arch?.update(s);
    this.monastery?.update(s);
    this.seventh?.update(s);
    this.taller?.update(s);
    this.biblio?.update(s);
    this.cable?.update(s);
    this.corazon?.update(s);
    this.ojos?.update(s);
    this.tormo?.update(s);
    this.caza?.update(s);
    this.vert?.update(s);
    this.bote?.update(s);
    this.isla?.update(s);
    this.roca?.update(s);
    this.camino?.update(s);
    this.pasarela?.update(s);
    this.nieve?.update(s);
    this.ermita?.update(s);
    this.vigilia?.update(s);
    this.druid?.update(s);
    this.hornos?.update(s);
    this.otono?.update(s);
    this.copa?.update(s);
    this.raices?.update(s);
    this.aster?.update(s);
    this.galo?.update(s);
    const now = performance.now();
    for (const e of s._events ?? []) {
      if (e.t !== 'say') continue;
      const line = t(`${this.info.key}.say.${e.line}`);
      // On the field: over that person. The hero (or a memory): over its name. Else a voice from off the field.
      if (s.senda?.npcs.some((n) => n.id === e.who)) {
        const old = this.said.find((x) => x.who === e.who);
        if (old) old.el.remove();
        this.said = this.said.filter((x) => x.who !== e.who);
        const el = h('div.senda-say', {}, h('b', {}, this.info.names[e.who] ?? ''), h('span', {}, line));
        this.bubbles.append(el);
        this.said.push({ who: e.who, el, until: now + SAY_MS, w: 0, h: 0 });
      } else if (e.who === 'hero' || s.heroes.some((x) => x.face === e.who)) this.heroSay(line);
      else {
        clear(this.voice);
        this.voice.append(h('b', {}, this.info.names[e.who] ?? ''), h('span', {}, `«${line}»`));
        this.voice.classList.add('on');
        this.voiceUntil = now + SAY_MS;
      }
    }
    if (this.voiceUntil && now > this.voiceUntil) {
      this.voiceUntil = 0;
      this.voice.classList.remove('on');
    }
  }

  private updateWedge(s: BattleState): void {
    const W = this.wedge;
    const w = s.senda?.wedge;
    const r = s.rules?.senda?.wedge;
    if (!W || !w || !r) return;
    const hand = Array.from({ length: r.shields }, (_, i) => `<i class="${i < w.hand ? 'on' : ''}">${ICONS.shield}</i>`).join('');
    if (W.hand.innerHTML !== hand) W.hand.innerHTML = hand;
    const count = Math.min(r.need, w.count);
    W.count.textContent = `${count}/${r.need}`;
    W.countFill.forEach((c, i) => c.classList.toggle('on', i < count));
    W.inside.textContent = `${w.entered}/${r.lose}`;
    W.insideFill.forEach((c, i) => c.classList.toggle('on', i < w.entered));
    // The column's clock: when it comes, then that it is coming, then the cornice down.
    const first = s.wave.spawns[0]?.tick ?? 0;
    const clock = w.down ? t('senda.wedge.fallen') : s.waveT < first ? t('senda.wedge.column', { s: Math.ceil((first - s.waveT) / TICK_HZ) }) : t('senda.wedge.coming');
    if (W.clock.textContent !== clock) W.clock.textContent = clock;
    W.clock.classList.toggle('soon', !w.down && s.waveT < first && first - s.waveT < 10 * TICK_HZ);
    // The marks: planted or open, the one the hero was sent to with its ring filling.
    const fillOf = (i: number): number => (w.at === i ? Math.min(1, w.held / Math.max(1, r.stand)) : 0);
    W.gaps.forEach((b, i) => {
      b.classList.toggle('shut', w.shut[i] === 1);
      b.classList.toggle('sent', w.at === i);
      b.style.setProperty('--k', fillOf(i).toFixed(2));
      b.disabled = s.result !== 'running';
    });
    W.rock.classList.toggle('sent', w.at === r.gaps.length);
    W.rock.classList.toggle('gone', w.pushed >= 0);
    W.rock.classList.toggle('full', w.count >= r.need);
    W.rock.style.setProperty('--k', fillOf(r.gaps.length).toFixed(2));
    const cb = W.cornice.firstChild as HTMLElement;
    if (cb.textContent !== String(count)) cb.textContent = String(count);
    W.cornice.classList.toggle('full', w.count >= r.need);
    W.cornice.classList.toggle('gone', w.down);
  }

  /** Every frame: the bubbles over whoever says them, and the ledge's marks over their places. */
  place(at: Map<string, THREE.Vector3>, camera: THREE.Camera, w: number, ht: number, anchors?: SendaAnchors | null, s?: BattleState, bridge?: BridgeAnchors | null, heroes?: (THREE.Vector3 | undefined)[], fort?: FortAnchors | null, slide?: SlideAnchors | null, pass?: PassAnchors | null, tags?: readonly TagBox[], altars?: AltarAnchors | null, salt?: SaltAnchors | null, arch?: ArchAnchors | null): void {
    if (this.bridge && s) this.bridge.place(s, camera, w, ht, bridge ?? null, heroes);
    this.altar?.place(camera, w, ht, altars ?? null);
    this.salt?.place(camera, w, ht, salt ?? null);
    this.arch?.place(camera, w, ht, arch ?? null);
    this.monastery?.place(camera, w, ht, at);
    this.seventh?.place(camera, w, ht);
    this.taller?.place(camera, w, ht);
    this.biblio?.place(camera, w, ht);
    this.cable?.place(camera, w, ht);
    this.corazon?.place(camera, w, ht);
    this.ojos?.place(camera, w, ht);
    this.tormo?.place(camera, w, ht);
    this.caza?.place(s, camera, w, ht);
    this.vert?.place(s, camera, w, ht);
    this.bote?.place(s, camera, w, ht);
    this.isla?.place(s, camera, w, ht);
    this.roca?.place(s, camera, w, ht);
    this.camino?.place(camera, w, ht);
    this.pasarela?.place(camera, w, ht);
    this.nieve?.place(camera, w, ht);
    this.ermita?.place(camera, w, ht);
    this.vigilia?.place(camera, w, ht);
    this.druid?.place(camera, w, ht);
    this.hornos?.place(camera, w, ht);
    this.otono?.place(camera, w, ht);
    this.copa?.place(camera, w, ht);
    this.raices?.place(camera, w, ht);
    this.aster?.place(camera, w, ht);
    this.galo?.place(camera, w, ht, s);
    this.fort?.place(camera, w, ht, fort ?? null);
    this.slide?.place(camera, w, ht, slide ?? null);
    if (this.pass && s) this.pass.place(s, camera, w, ht, pass ?? null, at);
    const W = this.wedge;
    if (W && anchors) {
      W.gaps.forEach((b, i) => this.pin(b, anchors.gaps[i], camera, w, ht));
      this.pin(W.cornice, anchors.cornice, camera, w, ht);
      this.pinRock(W.rock, anchors, camera, w, ht, heroes ?? []);
    }
    // No mark on a name: the heroes' tags and the bars over the path's people (game/mark-pin.ts).
    const boxes = this.tagBoxes;
    boxes.length = 0;
    if (tags) for (const b of tags) boxes.push(b);
    if (s) {
      const rules = s.rules?.senda;
      (s.senda?.npcs ?? []).forEach((n, i) => {
        if (rules?.npcs[i]?.safe || n.away || n.hp <= 0) return;
        const p = at.get(n.id);
        if (!p) return;
        this.v.copy(p).setY(p.y + 1.15).project(camera);
        if (this.v.z > 1) return;
        const x = (this.v.x * 0.5 + 0.5) * w;
        const y = (-this.v.y * 0.5 + 0.5) * ht;
        boxes.push({ x0: x - 16, y0: y - 4, x1: x + 16, y1: y + 4 });
      });
    }
    spreadMarks(this.marks);
    clearOfTags(this.marks, boxes);
    if (!this.said.length) return;
    // The bubbles keep clear of the marks too (where they stand now), not only of the names: a tap must stay in sight.
    markBoxes(this.marks, boxes);
    const now = performance.now();
    this.said = this.said.filter((x) => {
      if (x.until > now) return true;
      x.el.remove();
      return false;
    });
    // The plate and the place's banner: a bubble never goes under them (a bubble by the top edge of a phone sat half
    // under the plate). Read four times a second, not every frame.
    const cover = this.cover(now);
    for (const x of this.said) {
      const p = at.get(x.who);
      if (!p) {
        setStyle(x.el, 'display', 'none');
        continue;
      }
      this.v.copy(p).setY(p.y + 1.55).project(camera);
      if (this.v.z > 1) {
        setStyle(x.el, 'display', 'none');
        continue;
      }
      setStyle(x.el, 'display', '');
      // Its size read once, when first shown (no layout read per frame).
      if (!x.w) {
        x.w = x.el.offsetWidth;
        x.h = x.el.offsetHeight;
      }
      // Kept whole on the screen's sides, and over every name tag, life bar, mark and bubble already placed it would
      // cover (a bubble said beside the hero covered the hero's name on a phone; Piet's and his mother's covered each
      // other in the corral): up first, its tail still pointing down at whoever says it.
      const half = x.w / 2 + 6;
      const bx = Math.min(w - half, Math.max(half, (this.v.x * 0.5 + 0.5) * w));
      const by0 = (-this.v.y * 0.5 + 0.5) * ht;
      const hits = (b: TagBox, y: number): boolean => !(bx + x.w / 2 < b.x0 - 4 || bx - x.w / 2 > b.x1 + 4 || y + 6 < b.y0 - 4 || y - x.h > b.y1 + 4);
      let by = by0;
      // (Until nothing moves it: lifted over one box it may land on another, a mark over a name.)
      for (let pass = 0, moved = true; moved && pass < 6; pass++) {
        moved = false;
        for (const b of boxes) {
          if (!hits(b, by)) continue;
          by = b.y0 - 4 - 6;
          moved = true;
        }
      }
      // Up into the plate or the banner (or off the top): down instead, under them and under whatever it meets there.
      let under = by - x.h < 2 || cover.some((b) => hits(b, by));
      if (under) {
        by = by0;
        for (let pass = 0, moved = true; moved && pass < 8; pass++) {
          moved = false;
          for (const b of cover) {
            if (!hits(b, by)) continue;
            by = b.y1 + 4 + x.h;
            moved = true;
          }
          for (const b of boxes) {
            if (!hits(b, by)) continue;
            by = b.y1 + 4 + x.h + 6;
            moved = true;
          }
        }
        under = by !== by0;
      }
      x.el.classList.toggle('loose', under);
      setStyle(x.el, 'transform', `translate(${bx.toFixed(1)}px, ${by.toFixed(1)}px) translate(-50%, -100%)`);
      // The next bubble keeps clear of this one.
      boxes.push({ x0: bx - x.w / 2, y0: by - x.h, x1: bx + x.w / 2, y1: by + 6 });
    }
  }

  private coverBoxes: TagBox[] = [];
  private coverAt = 0;

  /** The plate's box and, while it shows, the place's banner's, in the bubbles' own frame (read at most every 250 ms). */
  private cover(now: number): TagBox[] {
    if (now - this.coverAt < 250) return this.coverBoxes;
    this.coverAt = now;
    const out = this.coverBoxes;
    out.length = 0;
    const o = this.bubbles.getBoundingClientRect();
    const add = (el: Element | null | undefined): void => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.width && r.height) out.push({ x0: r.left - o.left, y0: r.top - o.top, x1: r.right - o.left, y1: r.bottom - o.top });
    };
    add(this.el);
    const hud = this.el.parentElement;
    if (hud?.classList.contains('banner-on')) {
      add(hud.querySelector('.wave-banner .big'));
      add(hud.querySelector('.wave-banner .small'));
    }
    return out;
  }

  private tagBoxes: TagBox[] = [];
  private rockW = 0;
  private spots: number[] = [];

  /**
   * The rock's mark is a pill wider than the gaps' marks: its ring sits on the rock and its words run to whichever
   * side (right, left, above, below) covers no gap's mark nor the counter, so on a phone nothing to tap hides under it.
   */
  private pinRock(el: HTMLElement, a: SendaAnchors, camera: THREE.Camera, w: number, ht: number, heroes: (THREE.Vector3 | undefined)[]): void {
    if (!a.rock) return setStyle(el, 'display', 'none');
    this.v.copy(a.rock).project(camera);
    if (this.v.z > 1) return setStyle(el, 'display', 'none');
    setStyle(el, 'display', '');
    const x = (this.v.x * 0.5 + 0.5) * w;
    const y = (-this.v.y * 0.5 + 0.5) * ht;
    // Its width once laid out (read once, not every frame).
    if (!this.rockW) this.rockW = el.offsetWidth;
    const pw = this.rockW || 150;
    const ph = 44;
    const spots = this.spots;
    spots.length = 0;
    const n = a.gaps.length;
    for (let i = 0; i <= n; i++) {
      const g = i < n ? a.gaps[i] : a.cornice;
      if (!g) continue;
      this.v.copy(g).project(camera);
      spots.push((this.v.x * 0.5 + 0.5) * w, (-this.v.y * 0.5 + 0.5) * ht);
    }
    // The heroes' name tags over their heads (on a phone the pill covered the hero's own name).
    for (const p of heroes) {
      if (!p) continue;
      this.v.copy(p).setY(p.y + 1.35).project(camera);
      spots.push((this.v.x * 0.5 + 0.5) * w, (-this.v.y * 0.5 + 0.5) * ht);
    }
    // Candidate boxes: left, top of the pill.
    let bestX = x - 16;
    let bestY = y - ph / 2;
    let bestHit = Infinity;
    for (let k = 0; k < 4; k++) {
      const bx = k === 0 ? x - 16 : k === 1 ? x - pw + 16 : x - pw / 2;
      const by = k < 2 ? y - ph / 2 : k === 2 ? y - ph - 14 : y + 14;
      let hit = 0;
      for (let i = 0; i < spots.length; i += 2) {
        const sx = spots[i]!;
        const sy = spots[i + 1]!;
        // A gap's mark is 44 px round its point: overlap if within 22 px of the box.
        const dx = Math.max(bx - sx, 0, sx - (bx + pw));
        const dy = Math.max(by - sy, 0, sy - (by + ph));
        if (dx < 24 && dy < 24) hit++;
      }
      if (bx < 4 || bx + pw > w - 4) hit += 0.5;
      if (hit < bestHit) {
        bestHit = hit;
        bestX = bx;
        bestY = by;
      }
      if (hit === 0) break;
    }
    pinMark(el, bestX + pw / 2, bestY + ph / 2);
  }

  /** A mark at a world point (hidden off screen or behind the camera). */
  private pin(el: HTMLElement, p: THREE.Vector3 | null | undefined, camera: THREE.Camera, w: number, ht: number): void {
    if (!p) {
      setStyle(el, 'display', 'none');
      return;
    }
    this.v.copy(p).project(camera);
    if (this.v.z > 1) {
      setStyle(el, 'display', 'none');
      return;
    }
    setStyle(el, 'display', '');
    pinMark(el, (this.v.x * 0.5 + 0.5) * w, (-this.v.y * 0.5 + 0.5) * ht);
  }
}
